import React, { useState, useMemo, useEffect } from 'react'
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Paper,
  IconButton, Stack, ToggleButton, ToggleButtonGroup, TextField, MenuItem
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useAutoCoder from '../hooks/useAutoCoder'
import useFrameworkLibrary from '../hooks/useFrameworkLibrary'
import useCodingModels from '../hooks/useCodingModels'
import useSnackbar from '../hooks/useSnackbar'
import { useTeams } from '../hooks/useAdmin'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import {
  AutoCoderImportConfig,
  AutoCoderImportType,
  CarbonInterviewRawDataRow,
} from '../types/gotypes'
import {
  PipelineInfoStep,
  PipelineFrameworkStep,
  PipelinePromptSettingsStep,
} from '../components/autocoder/pipeline'
import {
  buildCreatePipelineArgs,
  billingTeamRequired,
  effectiveBillingTeam,
  type PipelineKind,
} from './autocoderCreatePayload'
import { useDisplayNames } from '../utils/displayNames'

// The wizard no longer collects preview rows (the Data Source step is retired),
// but the framework step still takes the prop — a stable empty array keeps it
// from re-rendering on every parent render.
const NO_PREVIEW_DATA: CarbonInterviewRawDataRow[] = []

const getDefaultConfig = (): AutoCoderImportConfig => ({
  name: '',
  description: '',
  prompt: '',
  import_type: AutoCoderImportType.manual,
  // No data_source: adding data is a job-level act on the Data tab (T20).
  framework_engine: {
    type: AutoCoderImportType.themeai,
    themeai: {
      themes: [],
      suggestions: [],
      messages: [],
      batch_size: 10,
      prompt: '',
    },
  },
  frameworks: [],
  accumulated_suggestions: [],
  total_cases: 0,
  prompt_settings: {
    batch_size: 50,
    include_explanations: false,
    combined_mode: true,
    max_retries: 2,
    model_version: '',
  },
})

const AutoCoderPipelineEditor: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const snackbar = useSnackbar()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const pipelineId = router.params.pipelineId || 'new'
  const labelsParam = router.params.labels as string | undefined

  // Parse labels from URL query param (format: "key1:value1,key2:value2")
  const initialLabels = useMemo(() => {
    if (!labelsParam) return {}
    const result: Record<string, string> = {}
    labelsParam.split(',').forEach(pair => {
      const [key, value] = pair.split(':')
      if (key && value) result[key.trim()] = value.trim()
    })
    return result
  }, [labelsParam])

  const autoCoder = useAutoCoder({ customer, job, pipelineId })
  const frameworkLibrary = useFrameworkLibrary({ customer, job })
  const codingModels = useCodingModels(customer, job)
  const { teams, teamsLoading } = useTeams(customer)
  const isNewPipeline = pipelineId === 'new'

  // This wizard CREATES pipelines. Changing an existing one belongs on that
  // pipeline's own Settings tab, which shows every setting at once instead of
  // three steps — and, being part of the pipeline page, has a back button that
  // knows where it came from (this one could only ever return to the list).
  //
  // The route is kept and redirected rather than deleted, so bookmarks and the
  // ⋮ menu entry that used to point here still land somewhere correct.
  useEffect(() => {
    if (!isNewPipeline && customer && job) {
      router.navigate('autocoder-pipeline', { customer, job, pipelineId, section: 'settings' })
    }
    // The trigger is WHICH pipeline this route names, not every value read
    // inside: router is a stable wrapper and re-running on its identity would
    // fight the navigation it just performed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewPipeline, customer, job, pipelineId])

  // Local state. Everything here starts empty: this page only ever builds a NEW
  // pipeline, so there is nothing to hydrate from.
  const [activeStep, setActiveStep] = useState(0)
  // The page helper (Douglas plan T16).
  const [helperOpen, setHelperOpen] = useState(false)
  const [localConfig, setLocalConfig] = useState<AutoCoderImportConfig>(getDefaultConfig())
  const [localLabels, setLocalLabels] = useState<Record<string, string>>({})

  // Pipeline kind and optional system name for long-lived
  const [pipelineKind, setPipelineKind] = useState<PipelineKind>('one-shot')
  const [systemName, setSystemName] = useState('')

  // The team the new pipeline's AI spend is billed to. Required when the
  // customer has an open team (Adam, 6 Oct: "We have to apportion usage and
  // spend to a particular team"), and pre-selected when there is only one.
  const [chosenBillingTeam, setChosenBillingTeam] = useState('')
  const openTeams = teams.filter((t) => t.archived_at == null)
  const billingTeamId = effectiveBillingTeam(teams, chosenBillingTeam)
  const needsBillingTeam = billingTeamRequired(teams)

  // The Data Source step is retired: adding data is a JOB-level act on the Data
  // tab, and a pipeline is only a name, a framework and its prompt settings.
  // (T20 finishes the retirement — the tiles, the handlers and the create
  // payload; this is the part the step file's deletion forced.)
  const steps = useMemo(
    () => ['Pipeline Info', 'Code Framework', 'Prompt Settings'],
    [],
  )

  // Apply initial labels from URL params (for new pipelines from ThemeAI page)
  if (Object.keys(localLabels).length === 0 && Object.keys(initialLabels).length > 0) {
    setLocalLabels(initialLabels)
  }

  // Derived state — long-lived pipelines require a non-empty system name
  const canSave =
    localConfig.name.trim() !== '' &&
    (pipelineKind !== 'long-lived' || systemName.trim() !== '') &&
    // Until the teams load nobody knows whether a team is required.
    !teamsLoading &&
    (!needsBillingTeam || billingTeamId !== '')
  // A pipeline created here has no records yet — data arrives afterwards, sent
  // in from the job's Data tab — so ThemeAI has nothing to read. This was
  // already the effective value before the edit branch was deleted (both of its
  // inputs were false for a new pipeline); it is stated plainly now rather than
  // derived from a pipeline that is never loaded. It is also why there is no
  // "start processing" action on this page (T6).
  const hasDataConfirmed = false

  // Handlers
  const handleBack = () => {
    // Navigate back to the appropriate list based on labels
    if (localLabels.type === 'themeai') {
      router.navigate('themeai', { customer, job })
    } else {
      router.navigate('autocoder', { customer, job })
    }
  }

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))
  }

  const handlePrevious = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0))
  }

  const handleSave = async () => {
    const args = buildCreatePipelineArgs(pipelineKind, localConfig, {
      systemName: systemName.trim() || undefined,
      labels: localLabels,
      billingTeamId,
    })
    const pipeline = await autoCoder.createPipeline(args.config, args.labels, {
      systemName: args.systemName,
      lifecycle: args.lifecycle,
    })
    // Created — hand over to the pipeline's own Settings tab, which is where
    // every later change is made.
    router.navigate('autocoder-pipeline', {
      customer, job, pipelineId: pipeline.id, section: 'settings',
    })
    snackbar.success('Pipeline created')
  }

  // Derive the current step's label from the dynamic steps array
  const currentStepLabel = steps[activeStep]

  // Voice form-filling: the user dictates the content, the assistant writes it
  // into the field through the same setter the input uses. Setting a field does
  // NOT save — the user reviews and saves, so a spoken value can't create the
  // pipeline on its own. pageKey is the route param (stable) so filling the
  // name doesn't re-register the session.
  // Douglas reads this back aloud, so it carries display names rather than
  // the codes in the URL (#1347 item 1).
  const names = useDisplayNames(customer)

  useVoiceRegistration({
    pageKey: `autocoder-pipeline:${customer}:${job}:${pipelineId}`,
    pageContext: () =>
      `AutoCoder pipeline wizard (new pipeline) for ${names.customer}/${names.job(job)}. ` +
      `Current step: ${currentStepLabel}. Pipeline name: ${localConfig.name ? `"${localConfig.name}"` : 'not set yet'}. ` +
      `You can fill the pipeline name and description from what the user dictates and save the pipeline ` +
      `when the user asks. A pipeline no longer picks a data source — data is added on the job's Data tab ` +
      `and sent to a pipeline from there. ` +
      `Saving stores the configuration; it does NOT start coding (that spends AI tokens) — leave running the pipeline to the user.`,
    uiTools: buildVoiceTools({
      fields: [
        { name: 'set_pipeline_name', label: 'the pipeline name', set: (name) => setLocalConfig((prev) => ({ ...prev, name })) },
        { name: 'set_pipeline_description', label: 'the pipeline description', set: (description) => setLocalConfig((prev) => ({ ...prev, description })) },
      ],
      actions: [
        {
          name: 'save_pipeline',
          label: 'save the pipeline',
          note: 'Stores the draft configuration; does NOT start coding or run anything.',
          enabled: () => canSave,
          run: () => handleSave(),
        },
      ],
    }),
  })

  // Editing an existing pipeline belongs on its Settings tab, and the effect at
  // the top of this component is already navigating there. Render nothing in the
  // meantime: showing the wizard would flash a second, competing edit surface
  // over the pipeline the user is being sent to. Placed after every hook so the
  // hook order is unconditional.
  if (!isNewPipeline) return null

  return (
    <Box sx={{
      p: 3,
      maxWidth: 1800,
      mx: 'auto',
      height: 'calc(100vh - 64px)', // Account for app bar
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          New Pipeline
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <PageAgentButton
            pageKey="pipeline-editor"
            customer={account.customer?.name || customer}
            open={helperOpen}
            onClick={() => setHelperOpen(o => !o)}
          />
        </Box>
      </Stack>

      {/* Pipeline-kind chooser */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            color="info"
            size="small"
            value={pipelineKind}
            onChange={(_, v) => {
              if (v) {
                setPipelineKind(v as PipelineKind)
                setActiveStep(0)
              }
            }}
          >
            <ToggleButton value="one-shot">One-shot</ToggleButton>
            <ToggleButton value="long-lived">Long-lived</ToggleButton>
          </ToggleButtonGroup>
          {pipelineKind === 'long-lived' && (
            <TextField
              label="System name"
              size="small"
              required
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              helperText="Stable handle sources target (e.g. sky-tiktok)"
            />
          )}
          {needsBillingTeam && (
            <TextField
              select
              label="Billing team"
              size="small"
              required
              value={billingTeamId}
              onChange={(e) => setChosenBillingTeam(e.target.value)}
              helperText="The team whose AI budget pays for this pipeline"
              sx={{ minWidth: 240 }}
              data-testid="autocoder-pipeline-billing-team"
            >
              {openTeams.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </TextField>
          )}
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 2, flexShrink: 0 }}>
        {steps.map((label, index) => (
          <Step key={label} onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Paper sx={{
        p: 3,
        mb: 2,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {currentStepLabel === 'Pipeline Info' && (
          <PipelineInfoStep
            name={localConfig.name}
            description={localConfig.description}
            labels={localLabels}
            onNameChange={(name) => setLocalConfig((prev) => ({ ...prev, name }))}
            onDescriptionChange={(description) => setLocalConfig((prev) => ({ ...prev, description }))}
            onLabelsChange={setLocalLabels}
            isNewPipeline
          />
        )}

        {currentStepLabel === 'Code Framework' && (
          <PipelineFrameworkStep
            frameworkEngine={localConfig.framework_engine}
            frameworks={localConfig.frameworks}
            hasDataConfirmed={hasDataConfirmed}
            previewData={NO_PREVIEW_DATA}
            // No source variable: only a pipeline built back when pipelines owned
            // a data source has one, and those are edited on Settings, not here.
            variable={undefined}
            customer={customer}
            job={job}
            pipelineId={pipelineId}
            onFrameworkEngineChange={(framework_engine) => setLocalConfig((prev) => ({ ...prev, framework_engine }))}
            onFrameworksChange={(frameworks) => setLocalConfig((prev) => ({ ...prev, frameworks }))}
            frameworkLibrary={frameworkLibrary.frameworks}
            frameworkLibraryLoading={frameworkLibrary.frameworksLoading}
          />
        )}

        {currentStepLabel === 'Prompt Settings' && (
          <PipelinePromptSettingsStep
            settings={localConfig.prompt_settings || {
              batch_size: 50,
              include_explanations: false,
              combined_mode: true,
              max_retries: 2,
              model_version: '',
            }}
            onChange={(prompt_settings) => setLocalConfig((prev) => ({ ...prev, prompt_settings }))}
            // Nothing has been sent to this pipeline yet, so any cost estimate
            // here is an estimate for zero records.
            recordCount={0}
            frameworkCount={localConfig.frameworks?.length || 0}
            loopedFrameworkCount={localConfig.frameworks?.filter(f => f.looped_variables?.length > 0).length || 0}
            models={codingModels.data?.models || []}
            modelsLoading={codingModels.loading}
            modelsError={codingModels.error}
            defaultModel={codingModels.data?.default}
          />
        )}
      </Paper>

      {/* Action Buttons */}
      <Stack direction="row" justifyContent="space-between" sx={{ flexShrink: 0 }}>
        <Button
          onClick={handlePrevious}
          disabled={activeStep === 0}
        >
          Previous
        </Button>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!canSave || autoCoder.createLoading}
          >
            Save
          </Button>

          {/* No "Save & Start Processing" here any more. It required
              hasDataConfirmed, and a pipeline being CREATED has no records by
              definition — data is sent in afterwards, from the job's Data tab —
              so the button could never be pressed. It was left disabled on the
              final step, reading as something the user had failed to unlock.
              Starting a pipeline belongs on the pipeline's own page, once it has
              something to code. */}
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              color="info"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Stack>
      </Stack>

      <PageAgentDrawer
        pageKey="pipeline-editor"
        customer={account.customer?.name || customer}
        job={job}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default AutoCoderPipelineEditor
